import { useEffect, useMemo, useState } from "react";

import AnimatedModal from "components/layout/motion/AnimatedModal";
import type {
    PostProcessCatalog,
    PostProcessPredicate,
    PostProcessStep,
} from "lib/consts/postProcessRules";
import {
    buildRelabelActions,
    colorFromActions,
    labelFromActions,
    newStepId,
} from "lib/consts/postProcessRules";

type PostProcessRuleDialogProps = {
    open: boolean;
    step: PostProcessStep | null;
    catalog: PostProcessCatalog | null;
    classLabels: string[];
    onClose: () => void;
    onSave: (step: PostProcessStep) => void;
};

const FILTER_TYPES = new Set([
    "filter_by_confidence",
    "filter_by_class",
    "filter_by_area",
    "filter_by_size",
    "filter_top_k_per_class",
    "filter_nms",
    "suppress_labels",
    "suppress_if_overlaps",
    "associate_best_overlap",
]);

const inputClass =
    "w-full rounded-r5 border border-black80 bg-black70 px-3 py-2 text-h5 text-white focus:border-primary2 focus:outline-none";

function parseLabels(raw: string): string[] {
    return raw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
}

const PostProcessRuleDialog = ({
    open,
    step,
    catalog,
    classLabels,
    onClose,
    onSave,
}: PostProcessRuleDialogProps) => {
    const [draft, setDraft] = useState<PostProcessStep | null>(null);

    useEffect(() => {
        if (open && step) {
            setDraft({ ...step });
        }
    }, [open, step]);

    const stepTypes = catalog?.step_types ?? [];

    const isRule = draft?.type === "rule";

    const labelOptions = useMemo(() => {
        const set = new Set(classLabels);
        (draft?.on_labels ?? []).forEach((l) => set.add(l));
        (draft?.labels ?? []).forEach((l) => set.add(l));
        (draft?.when?.with_labels ?? []).forEach((l) => set.add(l));
        return Array.from(set).sort();
    }, [classLabels, draft]);

    if (!open || !draft) {
        return null;
    }

    const updateDraft = (patch: Partial<PostProcessStep>) => {
        setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
    };

    const updateWhen = (patch: Partial<PostProcessPredicate>) => {
        setDraft((prev) =>
            prev
                ? {
                      ...prev,
                      when: { ...(prev.when ?? { predicate: "none_overlap" }), ...patch },
                  }
                : prev,
        );
    };

    const updateThen = (label: string, color: string) => {
        setDraft((prev) =>
            prev ? { ...prev, then: buildRelabelActions(label, color) } : prev,
        );
    };

    const updateElse = (label: string, color: string) => {
        setDraft((prev) =>
            prev
                ? {
                      ...prev,
                      else: label ? buildRelabelActions(label, color) : [],
                  }
                : prev,
        );
    };

    const handleSave = () => {
        if (!draft.name?.trim()) {
            updateDraft({ name: summarizeType(draft.type) });
        }
        onSave({ ...draft, id: draft.id || newStepId() });
        onClose();
    };

    return (
        <AnimatedModal visible={open} onClose={onClose} className="bg-black100">
            <div className="flex max-h-[85vh] w-[min(32rem,calc(100vw-2rem))] flex-col gap-4 overflow-y-auto p-4">
                <h2 className="text-h4 font-semibold text-white">
                    {step?.id ? "Edit post-processing rule" : "Add post-processing rule"}
                </h2>

                <label className="flex flex-col gap-1 text-h5 text-black40">
                    Name
                    <input
                        className={inputClass}
                        value={draft.name ?? ""}
                        onChange={(e) => updateDraft({ name: e.target.value })}
                        placeholder="Rule name"
                    />
                </label>

                <label className="flex flex-col gap-1 text-h5 text-black40">
                    Step type
                    <select
                        className={inputClass}
                        value={draft.type}
                        onChange={(e) => {
                            const type = e.target.value;
                            const next: PostProcessStep = {
                                ...draft,
                                type,
                                id: draft.id || newStepId(),
                                enabled: true,
                            };
                            if (type === "rule" && !next.when) {
                                next.when = {
                                    predicate: "none_overlap",
                                    with_labels: labelOptions.slice(0, 1),
                                    region: "head_top",
                                    region_fraction: 0.3,
                                    metric: "iou",
                                    threshold: 0.05,
                                };
                                next.then = buildRelabelActions("no hardhat person", "#ff4444");
                                next.on_labels = labelOptions.includes("person")
                                    ? ["person"]
                                    : labelOptions.slice(0, 1);
                            }
                            if (type === "suppress_labels" && !next.labels) {
                                next.labels = ["hardhat"];
                            }
                            setDraft(next);
                        }}
                    >
                        {stepTypes.map((st) => (
                            <option key={st.type} value={st.type}>
                                {st.label}
                            </option>
                        ))}
                    </select>
                </label>

                {isRule ? (
                    <>
                        <label className="flex flex-col gap-1 text-h5 text-black40">
                            On labels (comma-separated)
                            <input
                                className={inputClass}
                                value={(draft.on_labels ?? []).join(", ")}
                                onChange={(e) =>
                                    updateDraft({ on_labels: parseLabels(e.target.value) })
                                }
                            />
                        </label>
                        <label className="flex flex-col gap-1 text-h5 text-black40">
                            Predicate
                            <select
                                className={inputClass}
                                value={draft.when?.predicate ?? "none_overlap"}
                                onChange={(e) => updateWhen({ predicate: e.target.value })}
                            >
                                {(catalog?.predicates ?? []).map((p) => (
                                    <option key={p.type} value={p.type}>
                                        {p.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="flex flex-col gap-1 text-h5 text-black40">
                            With labels
                            <input
                                className={inputClass}
                                value={(draft.when?.with_labels ?? []).join(", ")}
                                onChange={(e) =>
                                    updateWhen({ with_labels: parseLabels(e.target.value) })
                                }
                            />
                        </label>
                        <label className="flex flex-col gap-1 text-h5 text-black40">
                            Region
                            <select
                                className={inputClass}
                                value={draft.when?.region ?? "full"}
                                onChange={(e) => updateWhen({ region: e.target.value })}
                            >
                                {(catalog?.regions ?? []).map((r) => (
                                    <option key={r.value} value={r.value}>
                                        {r.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="flex flex-col gap-1 text-h5 text-black40">
                            IoU threshold
                            <input
                                type="number"
                                min={0}
                                max={1}
                                step={0.01}
                                className={inputClass}
                                value={draft.when?.threshold ?? 0.05}
                                onChange={(e) =>
                                    updateWhen({ threshold: parseFloat(e.target.value) || 0 })
                                }
                            />
                        </label>
                        <label className="flex flex-col gap-1 text-h5 text-black40">
                            Then: relabel as
                            <input
                                className={inputClass}
                                value={labelFromActions(draft.then)}
                                onChange={(e) =>
                                    updateThen(
                                        e.target.value,
                                        colorFromActions(draft.then),
                                    )
                                }
                            />
                        </label>
                        <label className="flex flex-col gap-1 text-h5 text-black40">
                            Then: box color
                            <div className="flex items-center gap-2">
                                <input
                                    type="color"
                                    className="h-10 w-12 cursor-pointer rounded border border-black80 bg-black70"
                                    value={
                                        colorFromActions(draft.then) || "#ff4444"
                                    }
                                    onChange={(e) =>
                                        updateThen(
                                            labelFromActions(draft.then),
                                            e.target.value,
                                        )
                                    }
                                />
                                <input
                                    className={inputClass}
                                    value={colorFromActions(draft.then)}
                                    placeholder="#ff4444"
                                    onChange={(e) =>
                                        updateThen(
                                            labelFromActions(draft.then),
                                            e.target.value,
                                        )
                                    }
                                />
                            </div>
                        </label>
                        <label className="flex flex-col gap-1 text-h5 text-black40">
                            Else: relabel as (optional)
                            <input
                                className={inputClass}
                                value={labelFromActions(draft.else)}
                                onChange={(e) =>
                                    updateElse(
                                        e.target.value,
                                        colorFromActions(draft.else),
                                    )
                                }
                            />
                        </label>
                        <label className="flex flex-col gap-1 text-h5 text-black40">
                            Else: box color (optional)
                            <div className="flex items-center gap-2">
                                <input
                                    type="color"
                                    className="h-10 w-12 cursor-pointer rounded border border-black80 bg-black70"
                                    value={
                                        colorFromActions(draft.else) || "#00e096"
                                    }
                                    disabled={!labelFromActions(draft.else)}
                                    onChange={(e) =>
                                        updateElse(
                                            labelFromActions(draft.else),
                                            e.target.value,
                                        )
                                    }
                                />
                                <input
                                    className={inputClass}
                                    value={colorFromActions(draft.else)}
                                    placeholder="#00e096"
                                    disabled={!labelFromActions(draft.else)}
                                    onChange={(e) =>
                                        updateElse(
                                            labelFromActions(draft.else),
                                            e.target.value,
                                        )
                                    }
                                />
                            </div>
                        </label>
                    </>
                ) : null}

                {draft.type === "suppress_labels" ? (
                    <label className="flex flex-col gap-1 text-h5 text-black40">
                        Labels to hide
                        <input
                            className={inputClass}
                            value={(draft.labels ?? []).join(", ")}
                            onChange={(e) => updateDraft({ labels: parseLabels(e.target.value) })}
                        />
                    </label>
                ) : null}

                {FILTER_TYPES.has(draft.type) && draft.type === "filter_by_area" ? (
                    <label className="flex flex-col gap-1 text-h5 text-black40">
                        Min area (px²)
                        <input
                            type="number"
                            className={inputClass}
                            value={draft.min_area ?? 0}
                            onChange={(e) =>
                                updateDraft({ min_area: parseInt(e.target.value, 10) || 0 })
                            }
                        />
                    </label>
                ) : null}

                {draft.type === "filter_top_k_per_class" ? (
                    <label className="flex flex-col gap-1 text-h5 text-black40">
                        Top K
                        <input
                            type="number"
                            min={1}
                            className={inputClass}
                            value={draft.k ?? 5}
                            onChange={(e) => updateDraft({ k: parseInt(e.target.value, 10) || 1 })}
                        />
                    </label>
                ) : null}

                <div className="flex justify-end gap-2 pt-2">
                    <button
                        type="button"
                        className="rounded-r5 border border-black80 px-4 py-2 text-h5 text-white hover:bg-black80"
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="rounded-r5 border border-primary2 bg-primary2 px-4 py-2 text-h5 text-white hover:bg-primary1"
                        onClick={handleSave}
                    >
                        Save
                    </button>
                </div>
            </div>
        </AnimatedModal>
    );
};

function summarizeType(type: string): string {
    return type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export default PostProcessRuleDialog;
